import type { vendorApiNames } from '../names';
import type { ApiKeyAuthentication, ApiSecretAuthentication } from './auth';
import type {
  ApiVersion,
  BaseUpdateVendorApiSettings,
  BaseVendorApi,
} from './base';

export interface AwsAssumeRole {
  role: string;
}

export interface AwsConnectionSettings {
  /**
   * Assumed role default expiration in seconds. It is applied only when AWS provides no expiration value. When 0 (zero) is set, the assumed role will never expire.
   */
  assumedRoleExpirationDefaultSec?: number;
  /**
   * When an assumed role has its expiration, the client has to reassume it some time before it expires. The `reassumeRoleSafetyFactorPct` sets a number
   * of percent of the assumed role expiration when the first attempt to re-assume the role should be sent.
   */
  reassumeRoleSafetyFactorPct?: number;
  /**
   * Max number of requests attempts when `RequestLimitExceeded` error is received.
   */
  requestLimitExceededMaxAttempts?: number;
  /**
   * AWS regions
   */
  regions: string[];
}

type AwsAssumeRoleSettings = {
  /**
   * AWS assume roles
   */
  assumeRoles?: AwsAssumeRole[];
};

export type AwsAuthentication = ApiKeyAuthentication &
  ApiSecretAuthentication &
  AwsAssumeRoleSettings;

/**
 * AWS API settings available from API
 */
export type PublicAwsApiSettings = BaseVendorApi<
  (typeof vendorApiNames)['aws']
> &
  ApiVersion &
  AwsConnectionSettings &
  AwsAssumeRoleSettings;

/**
 * AWS API settings available in backend
 */
export type AwsApiSettings = PublicAwsApiSettings & AwsAuthentication;

/**
 * AWS API settings expected on API when updating
 */
export type UpdateAwsApiSettings = BaseUpdateVendorApiSettings<
  PublicAwsApiSettings,
  AwsAuthentication
>;
